import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../DataLayouts/Header";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import axios from "axios";

const emptyContact = (isPrimary = true) => ({
  organization_recruitment_source_contact_id: null,
  contact_person_name: "",
  designation: "",
  phone_number: "",
  alternate_phone_number: "",
  email: "",
  is_primary: isPrimary,
  is_active: true,
});

function RecruitmentSourcesForm({ mode }) {
  const { id } = useParams();
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    organization_recruitment_source_type_id: "",
    recruitment_source_name: "",
    website_url: "",
    address: "",
    description: "",
    previous_recruitment_count: 0,
    system_start_date: "",
    is_active: true,
    contacts: [emptyContact(true)],
  });
  const [types, setTypes] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(mode === "edit" || mode === "view");
  const [btnLoading, setBtnLoading] = useState(false);

  const namedContactCount = (formData.contacts || []).filter((c) =>
    c.contact_person_name?.trim()
  ).length;
  const showPrimaryPicker = (formData.contacts || []).length > 1;

  useEffect(() => {
    const loadTypes = async () => {
      if (!org?.organization_id) return;
      try {
        const res = await axios.get(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-source-type?active_only=1`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setTypes(res.data?.recruitment_source_types || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load source types");
      }
    };
    loadTypes();
  }, [org?.organization_id]);

  useEffect(() => {
    const loadById = async () => {
      try {
        const response = await axios.get(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-source/${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const a = response?.data?.recruitment_source || {};
        setFormData({
          organization_recruitment_source_type_id:
            a.organization_recruitment_source_type_id || "",
          recruitment_source_name: a.recruitment_source_name || "",
          website_url: a.website_url || "",
          address: a.address || "",
          description: a.description || "",
          previous_recruitment_count: a.previous_recruitment_count ?? 0,
          system_start_date: a.system_start_date
            ? String(a.system_start_date).slice(0, 10)
            : "",
          is_active: a.is_active == 1 || a.is_active === true,
          contacts:
            Array.isArray(a.contacts) && a.contacts.length > 0
              ? (() => {
                  const mapped = a.contacts.map((c) => ({
                    organization_recruitment_source_contact_id:
                      c.organization_recruitment_source_contact_id || null,
                    contact_person_name: c.contact_person_name || "",
                    designation: c.designation || "",
                    phone_number: c.phone_number || "",
                    alternate_phone_number: c.alternate_phone_number || "",
                    email: c.email || "",
                    is_primary: !!c.is_primary,
                    is_active: c.is_active == null ? true : !!c.is_active,
                  }));
                  if (mapped.length === 1) {
                    mapped[0].is_primary = true;
                  }
                  return mapped;
                })()
              : [emptyContact(true)],
        });
      } catch (err) {
        console.error(err);
        toast.error("Failed to load recruitment source");
      } finally {
        setLoading(false);
      }
    };

    if ((mode === "edit" || mode === "view") && id && org?.organization_id) {
      setLoading(true);
      loadById();
    }
  }, [mode, id, org?.organization_id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const normalizeContactsPrimary = (contacts) => {
    if (contacts.length <= 1) {
      return contacts.map((c) => ({ ...c, is_primary: true }));
    }
    return contacts;
  };

  const handleContactChange = (index, field, value) => {
    setFormData((prev) => {
      let contacts = [...prev.contacts];
      contacts[index] = { ...contacts[index], [field]: value };
      if (field === "is_primary" && value) {
        contacts = contacts.map((c, i) => ({
          ...c,
          is_primary: i === index,
        }));
      }
      return { ...prev, contacts: normalizeContactsPrimary(contacts) };
    });
    if (field === "is_primary") {
      setFormErrors((prev) => ({ ...prev, contacts: "" }));
    }
  };

  const addContact = () => {
    setFormData((prev) => ({
      ...prev,
      contacts: [...prev.contacts, emptyContact(false)],
    }));
  };

  const removeContact = (index) => {
    setFormData((prev) => {
      const next =
        prev.contacts.length <= 1
          ? [emptyContact(true)]
          : prev.contacts.filter((_, i) => i !== index);
      return { ...prev, contacts: normalizeContactsPrimary(next) };
    });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.organization_recruitment_source_type_id) {
      errors.organization_recruitment_source_type_id = "Source type is required.";
    }
    if (!formData.recruitment_source_name?.trim()) {
      errors.recruitment_source_name = "Source name is required.";
    }

    const namedContacts = (formData.contacts || []).filter((c) =>
      c.contact_person_name?.trim()
    );
    if (namedContacts.length > 1 && !namedContacts.some((c) => c.is_primary)) {
      errors.contacts = "Please select which contact is primary.";
    }

    setFormErrors(errors);
    if (errors.contacts) {
      toast.error(errors.contacts);
    }
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);

    let contacts = (formData.contacts || [])
      .filter((c) => c.contact_person_name?.trim())
      .map((c) => ({
        ...c,
        is_primary: !!c.is_primary,
        is_active: c.is_active !== false,
      }));

    if (contacts.length === 1) {
      contacts = contacts.map((c) => ({ ...c, is_primary: true }));
    }

    const payload = {
      organization_recruitment_source_type_id:
        formData.organization_recruitment_source_type_id,
      recruitment_source_name: formData.recruitment_source_name,
      website_url: formData.website_url || null,
      address: formData.address || null,
      description: formData.description || null,
      previous_recruitment_count: Number(formData.previous_recruitment_count) || 0,
      system_start_date: formData.system_start_date || null,
      is_active: !!formData.is_active,
      contacts,
    };

    try {
      if (mode === "edit") {
        await axios.put(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/recruitment-source/${id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      } else {
        await axios.post(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/recruitment-source`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }

      toast.success(
        mode === "edit"
          ? "Recruitment Source Updated!"
          : "Recruitment Source Created!"
      );
      navigate(-1);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        setFormErrors(validationErrors);
        const errorMessages = Object.values(validationErrors)
          .map((arr) => (Array.isArray(arr) ? arr[0] : arr))
          .join(" ");
        toast.error(errorMessages || "Validation failed.");
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <Header
      mode={mode}
      updateMessage={"Recruitment Source"}
      addMessage={"Recruitment Source"}
      homeLink={"/recruitment/sources"}
      homeText={"Recruitment Sources"}
      onSubmit={handleSubmit}
      loading={loading}
      btnLoading={btnLoading}
    >
      {loading ? (
        <Grid container spacing={2}>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={10}>
            <Paper elevation={4} sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Source Type"
                    name="organization_recruitment_source_type_id"
                    disabled={mode === "view"}
                    value={formData.organization_recruitment_source_type_id}
                    onChange={handleChange}
                    error={!!formErrors.organization_recruitment_source_type_id}
                    helperText={
                      formErrors.organization_recruitment_source_type_id ||
                      (types.length === 0
                        ? "No types found. Seed organization_recruitment_source_types first."
                        : "")
                    }
                    required
                  >
                    {types.map((t) => (
                      <MenuItem
                        key={t.organization_recruitment_source_type_id}
                        value={t.organization_recruitment_source_type_id}
                      >
                        {t.recruitment_source_type_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Source Name"
                    name="recruitment_source_name"
                    disabled={mode === "view"}
                    value={formData.recruitment_source_name}
                    onChange={handleChange}
                    error={!!formErrors.recruitment_source_name}
                    helperText={formErrors.recruitment_source_name}
                    required
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Website URL"
                    name="website_url"
                    disabled={mode === "view"}
                    value={formData.website_url}
                    onChange={handleChange}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Previous Recruitment Count"
                    name="previous_recruitment_count"
                    disabled={mode === "view"}
                    value={formData.previous_recruitment_count}
                    onChange={handleChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="System Start Date"
                    name="system_start_date"
                    disabled={mode === "view"}
                    value={formData.system_start_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.is_active}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            is_active: e.target.checked,
                          }))
                        }
                        disabled={mode === "view"}
                      />
                    }
                    label="Active"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    disabled={mode === "view"}
                    value={formData.address}
                    onChange={handleChange}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    disabled={mode === "view"}
                    multiline
                    minRows={3}
                    maxRows={5}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <Box>
                      <Typography variant="h6">Contacts</Typography>
                      {showPrimaryPicker && (
                        <Typography variant="caption" color="text.secondary">
                          Select which contact is primary
                          {formErrors.contacts
                            ? ` — ${
                                Array.isArray(formErrors.contacts)
                                  ? formErrors.contacts[0]
                                  : formErrors.contacts
                              }`
                            : ""}
                        </Typography>
                      )}
                      {!showPrimaryPicker && namedContactCount <= 1 && (
                        <Typography variant="caption" color="text.secondary">
                          Single contact is primary by default
                        </Typography>
                      )}
                    </Box>
                    {mode !== "view" && (
                      <Button
                        startIcon={<AddIcon />}
                        onClick={addContact}
                        size="small"
                        variant="outlined"
                      >
                        Add Contact
                      </Button>
                    )}
                  </Box>
                </Grid>

                {formData.contacts.map((contact, index) => (
                  <Grid item xs={12} key={index}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                        mb={1.5}
                      >
                        <Typography variant="subtitle2" fontWeight={700}>
                          Contact {index + 1}
                        </Typography>
                        {mode !== "view" && (
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => removeContact(index)}
                            aria-label={`Delete contact ${index + 1}`}
                            title="Delete contact"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Contact Person"
                            disabled={mode === "view"}
                            value={contact.contact_person_name}
                            onChange={(e) =>
                              handleContactChange(
                                index,
                                "contact_person_name",
                                e.target.value
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Designation"
                            disabled={mode === "view"}
                            value={contact.designation}
                            onChange={(e) =>
                              handleContactChange(
                                index,
                                "designation",
                                e.target.value
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Email"
                            disabled={mode === "view"}
                            value={contact.email}
                            onChange={(e) =>
                              handleContactChange(index, "email", e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Phone"
                            disabled={mode === "view"}
                            value={contact.phone_number}
                            onChange={(e) =>
                              handleContactChange(
                                index,
                                "phone_number",
                                e.target.value
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Alternate Phone"
                            disabled={mode === "view"}
                            value={contact.alternate_phone_number}
                            onChange={(e) =>
                              handleContactChange(
                                index,
                                "alternate_phone_number",
                                e.target.value
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          {showPrimaryPicker ? (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!contact.is_primary}
                                  disabled={mode === "view"}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      handleContactChange(
                                        index,
                                        "is_primary",
                                        true
                                      );
                                    }
                                  }}
                                />
                              }
                              label="Primary"
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ pt: 1 }}
                            >
                              Primary (default)
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    onClick={handleSubmit}
                    disabled={loading || btnLoading || mode === "view"}
                    sx={{
                      borderRadius: 2,
                      minWidth: 120,
                      textTransform: "capitalize",
                      fontWeight: 500,
                      mt: 1,
                    }}
                  >
                    {btnLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Submit"
                    )}
                  </Button>
                  {mode === "edit" && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={() => navigate(-1)}
                      sx={{
                        borderRadius: 2,
                        minWidth: 120,
                        textTransform: "capitalize",
                        fontWeight: 500,
                        ml: 2,
                        mt: 1,
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Header>
  );
}

export default RecruitmentSourcesForm;
